/**
 * DeviceDiagnostics — the device sheet's Diagnostics tab (OP #1663).
 *
 * Lists the catalogue entries THIS viewer may run (the server filters by role and re-checks
 * everything on each run), runs one at a time — the server allows one diagnostic per device —
 * and shows the redacted output in a monospace readout with copy, the facts strip (duration,
 * secrets hidden, truncation, audit row) and a Download support bundle action.
 *
 * Design: Meridian tokens only (surface/line/ink, healthy/critical for the readout's status
 * rule), tablet-first (44px targets via Button, full-width rows in the max-w-md sheet), light
 * and dark through the CSS variables. The readout is the one distinctive element: a status
 * rule down its left edge so a failed run is recognisable before reading a word.
 */
import { useState } from "react";
import { ApiError } from "@/lib/api/client";
import { useDiagnosticsBundle, useDiagnosticsCatalogue, useRunDiagnostic } from "@/lib/api/hooks";
import {
  TRANSPORT_LABEL, bundleFilename, deviceSessionChecksHidden, formatBytes, paramValid,
  refusalMessage, resultFacts,
  type DiagnosticEntry, type DiagnosticResult,
} from "@/lib/diagnostics";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

/** Device kinds the catalogue covers (services/diagnostics.KINDS). */
const DIAGNOSTIC_KINDS = new Set(["gateway", "switch", "access_point"]);

interface Outcome {
  result?: DiagnosticResult;   // a run that came back (ok or failed on the device side)
  refusal?: string;            // refused before running (role, rate limit, busy, params)
}

export function DeviceDiagnostics({ serial, kind }: { serial: string; kind: string }) {
  const supported = DIAGNOSTIC_KINDS.has(kind);
  const catalogue = useDiagnosticsCatalogue(supported ? kind : undefined);
  const run = useRunDiagnostic(serial);
  const bundle = useDiagnosticsBundle(serial);
  const [outcomes, setOutcomes] = useState<Record<string, Outcome>>({});
  const [params, setParams] = useState<Record<string, Record<string, string>>>({});
  const [running, setRunning] = useState<string | null>(null);
  const [bundleError, setBundleError] = useState<string | null>(null);

  if (!supported) {
    return <p className="text-sm text-ink-muted">There are no diagnostics for this kind of device yet.</p>;
  }
  if (catalogue.isLoading) return <p className="text-sm text-ink-muted">Loading diagnostics…</p>;
  if (catalogue.error) {
    return <p className="text-sm text-critical">Could not load the diagnostics list. Reopen the tab to retry.</p>;
  }
  const entries = catalogue.data?.items ?? [];
  if (entries.length === 0) {
    return <p className="text-sm text-ink-muted">Your role has no diagnostics for this device.</p>;
  }

  const runEntry = (entry: DiagnosticEntry) => {
    setRunning(entry.name);
    run.mutate(
      { name: entry.name, params: params[entry.name] },
      {
        onSuccess: (result) => setOutcomes((o) => ({ ...o, [entry.name]: { result } })),
        onError: (err) => {
          // A device-side failure (502/409 with a result body) still carries output + audit id.
          const body = err instanceof ApiError ? (err.body as Partial<DiagnosticResult>) : undefined;
          const outcome: Outcome =
            body && typeof body === "object" && "audit_id" in body && "duration_ms" in body
              ? { result: body as DiagnosticResult }
              : { refusal: refusalMessage(err instanceof ApiError ? err.status : 0, err instanceof ApiError ? err.body : null) };
          setOutcomes((o) => ({ ...o, [entry.name]: outcome }));
        },
        onSettled: () => setRunning(null),
      },
    );
  };

  const downloadBundle = () => {
    setBundleError(null);
    bundle.mutate(undefined, {
      onSuccess: ({ blob, disposition }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = bundleFilename(disposition, serial);
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      },
      onError: (err) =>
        setBundleError(refusalMessage(err instanceof ApiError ? err.status : 0, err instanceof ApiError ? err.body : null)),
    });
  };

  const bundled = catalogue.data?.bundle.entries.length ?? 0;
  const busy = running !== null || bundle.isPending;

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-muted">
        Read-only checks that run live on this device. Every run is recorded in the audit log, and
        passwords, keys and tokens are hidden before you see the output.
      </p>
      {/* Site operator on a gateway/switch: the server only lists controller-state reads
          (gateway shell / switch CLI checks are org_admin+, option A). Say so plainly. */}
      {deviceSessionChecksHidden(kind, entries) && (
        <p className="text-xs text-ink-subtle">
          Checks that sign in to the device itself (gateway shell, switch CLI) need an org admin.
        </p>
      )}

      {/* Support bundle: the fixed set for this device kind, as one sanitized JSON file. */}
      {bundled > 0 && (
        <div className="flex flex-col gap-1">
          <Button variant="primary" className="w-full" disabled={busy} onClick={downloadBundle}>
            {bundle.isPending ? "Building support bundle…" : "Download support bundle"}
          </Button>
          <p className="text-xs text-ink-subtle">
            {bundled} check{bundled === 1 ? "" : "s"} in one JSON file for support. Takes up to a minute.
          </p>
          {bundleError && <p className="text-xs text-critical" role="alert">{bundleError}</p>}
        </div>
      )}

      <ul className="flex flex-col gap-3">
        {entries.map((entry) => {
          const values = params[entry.name] ?? {};
          const paramsOk = entry.params.every((p) => paramValid(p, values[p.name] ?? ""));
          const outcome = outcomes[entry.name];
          return (
            <li key={entry.name} className="rounded border border-line bg-surface p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink">{entry.title}</div>
                  <div className="mt-0.5 font-mono text-xs text-ink-subtle">
                    {TRANSPORT_LABEL[entry.transport]} · up to {entry.timeout_s} s
                  </div>
                </div>
                <Button
                  variant="secondary"
                  className="shrink-0"
                  disabled={busy || !paramsOk}
                  onClick={() => runEntry(entry)}
                  aria-label={`Run ${entry.title}`}
                >
                  {running === entry.name ? "Running…" : "Run"}
                </Button>
              </div>
              <p className="mt-1 text-xs text-ink-muted">{entry.description}</p>

              {entry.params.map((p) => {
                const value = values[p.name] ?? "";
                const invalid = value !== "" && !paramValid(p, value);
                return (
                  <label key={p.name} className="mt-2 flex flex-col gap-1 text-xs text-ink-muted">
                    <span>
                      {p.name === "port" ? "Port" : p.name} <span className="text-ink-subtle">(e.g. {p.example})</span>
                    </span>
                    <input
                      className={cn(
                        "min-h-touch rounded border bg-surface px-3 font-mono text-sm text-ink",
                        invalid ? "border-critical" : "border-line",
                      )}
                      inputMode="text"
                      autoComplete="off"
                      spellCheck={false}
                      maxLength={32}
                      value={value}
                      aria-invalid={invalid}
                      onChange={(e) =>
                        setParams((s) => ({ ...s, [entry.name]: { ...values, [p.name]: e.target.value.trim() } }))
                      }
                    />
                    {invalid && <span className="text-critical">{p.description}</span>}
                  </label>
                );
              })}

              {outcome?.refusal && (
                <p className="mt-2 rounded bg-critical-soft px-3 py-2 text-xs text-critical" role="alert">
                  {outcome.refusal}
                </p>
              )}
              {outcome?.result && <Readout result={outcome.result} entry={entry} />}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** The output viewer: status rule, facts strip, copy, monospace scroll box, truncation note. */
function Readout({ result, entry }: { result: DiagnosticResult; entry: DiagnosticEntry }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.output);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <div
      className={cn(
        "mt-3 rounded border-l-4 bg-surface-2",
        result.ok ? "border-healthy" : "border-critical",
      )}
    >
      <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
        <span className="font-mono text-xs text-ink-muted">
          <span className={result.ok ? "text-healthy" : "text-critical"}>{result.ok ? "done" : "failed"}</span>
          {" · "}
          {resultFacts(result, entry).join(" · ")}
        </span>
        {result.output && (
          <Button variant="ghost" className="px-3 text-xs" onClick={copy}>
            {copied ? "Copied" : "Copy"}
          </Button>
        )}
      </div>
      {result.error && (
        <p className="px-3 pb-2 text-xs text-critical">{result.error.message}</p>
      )}
      {result.output && (
        <pre
          className="max-h-80 overflow-auto whitespace-pre px-3 pb-3 font-mono text-xs leading-relaxed text-ink"
          tabIndex={0}
          aria-label={`${entry.title} output`}
        >
          {result.output}
        </pre>
      )}
      {result.truncated && (
        <p className="border-t border-line px-3 py-2 text-xs text-warn">
          Output was cut at {formatBytes(entry.max_output_bytes)}{entry.tail_lines ? "; the newest lines are kept" : "; the first part is shown"}.
        </p>
      )}
    </div>
  );
}
